/**
 * Quadro Kanban de pedidos — colunas por status.
 *
 * - Arrastar (HTML5 DnD, desktop) entre colunas muda o status; dentro da
 *   coluna reordena manualmente (persistida via PATCH /orders/reorder)
 * - Clicar nos botões do cartão continua funcionando (touch-friendly)
 * - Ordem na coluna: posicionados manualmente primeiro, depois chegada
 */
import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { OrderDetailed } from '../lib/api.js'
import { cn } from '../lib/utils.js'
import { useI18n } from '../lib/i18n.js'

export type KanbanStatus = OrderDetailed['status']

export type KanbanColumn = {
  id: string
  status: KanbanStatus
  label: string
  /** Membros da coluna; padrão = status exato. Colunas derivadas filtram. */
  match?: (order: OrderDetailed) => boolean
  /** Acento visual (classes tailwind); padrão = acento do status. */
  accent?: string
}

type KanbanProps = {
  orders: OrderDetailed[]
  columns: KanbanColumn[]
  /** Valida se o drag/card pode mover o pedido para o status (transições). */
  canMove: (order: OrderDetailed, target: KanbanStatus) => boolean
  onMove: (order: OrderDetailed, target: KanbanStatus) => void
  /** Nova sequência completa da coluna após reordenação manual. */
  onReorder: (status: KanbanStatus, orderedIds: string[]) => void
  /**
   * Agrupamento visual em dois níveis, SEM priorizar: apenas execuções
   * ADJACENTES na sequência agrupam (a ordem manual/chegada e o
   * cronômetro de cada pedido nunca mudam).
   * - groupKey: nível 1 (ex.: Mesa 4 / Take-away / Delivery)
   * - subgroupKey: nível 2 (ex.: referência do cliente na mesa)
   */
  groupKey?: (order: OrderDetailed) => string | null
  subgroupKey?: (order: OrderDetailed) => string | null
  renderCard: (order: OrderDetailed) => ReactNode
}

/** Agrupa execuções adjacentes com a mesma chave — sem reordenar nada. */
function groupAdjacent<T>(list: T[], keyOf: (item: T) => string | null) {
  const groups: Array<{ key: string | null; items: T[] }> = []
  for (const item of list) {
    const key = keyOf(item)
    const last = groups[groups.length - 1]
    if (last && last.key !== null && last.key === key) {
      last.items.push(item)
    } else {
      groups.push({ key, items: [item] })
    }
  }
  return groups
}

/** Ordena uma coluna: manual (sort_order) primeiro, chegada depois. */
function columnSort(a: OrderDetailed, b: OrderDetailed): number {
  const aManual = a.sort_order !== null
  const bManual = b.sort_order !== null
  if (aManual && bManual) return a.sort_order! - b.sort_order!
  if (aManual) return -1
  if (bManual) return 1
  return a.created_at - b.created_at
}

/** Acento visual por status — identifica a coluna de longe. */
const COLUMN_ACCENT: Record<KanbanStatus, string> = {
  pending: 'bg-amber-100 text-amber-900 border-amber-300',
  confirmed: 'bg-blue-100 text-blue-900 border-blue-300',
  preparing: 'bg-violet-100 text-violet-900 border-violet-300',
  ready: 'bg-green-100 text-green-900 border-green-300',
  delivered: 'bg-teal-100 text-teal-900 border-teal-300',
  cancelled: 'bg-red-100 text-red-900 border-red-300',
  // fluxo garçom (0085) — sem coluna no kanban (visão por mesa do garçom)
  waiter_sent: 'bg-amber-100 text-amber-900 border-amber-300',
  waiter_ack: 'bg-blue-100 text-blue-900 border-blue-300',
  relayed: 'bg-teal-100 text-teal-900 border-teal-300',
}

function columnOf(col: KanbanColumn, order: OrderDetailed): boolean {
  return col.match ? col.match(order) : order.status === col.status
}

export function OrdersKanban({
  orders,
  columns,
  canMove,
  onMove,
  onReorder,
  groupKey,
  subgroupKey,
  renderCard,
}: KanbanProps) {
  const { t } = useI18n()
  const [dragId, setDragId] = useState<string | null>(null)
  const [overColumn, setOverColumn] = useState<string | null>(null)
  const [dropHint, setDropHint] = useState<string | null>(null) // id do cartão-alvo

  const byColumn = useMemo(() => {
    const map = new Map<string, OrderDetailed[]>()
    for (const col of columns) {
      map.set(col.id, orders.filter((o) => columnOf(col, o)).sort(columnSort))
    }
    return map
  }, [orders, columns])

  const dragged = dragId ? orders.find((o) => o.id === dragId) : null

  function handleDrop(target: KanbanColumn) {
    setOverColumn(null)
    setDropHint(null)
    if (!dragId || !dragged) return

    const sourceColumn = columns.find((c) => columnOf(c, dragged))

    if (sourceColumn?.id === target.id) {
      // reordenação dentro da coluna: insere na posição do cartão-alvo
      const list = byColumn.get(target.id) ?? []
      const ids = list.map((o) => o.id).filter((id) => id !== dragId)
      if (dropHint && dropHint !== dragId) {
        const at = ids.indexOf(dropHint)
        ids.splice(at >= 0 ? at : ids.length, 0, dragId)
      } else {
        ids.push(dragId)
      }
      const changed = ids.some((id, i) => {
        const o = orders.find((x) => x.id === id)
        return o?.sort_order !== i
      })
      if (changed) onReorder(target.status, ids)
    } else if (dragged.status !== target.status && canMove(dragged, target.status)) {
      // colunas derivadas do mesmo status (ex.: A pagar/Entregues) não são
      // alcançáveis por drag — a fronteira entre elas é o pagamento
      onMove(dragged, target.status)
    }
    setDragId(null)
  }

  return (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
    >
      {columns.map((col) => {
        const list = byColumn.get(col.id) ?? []
        return (
          <div
            key={col.id}
            onDragOver={(e) => {
              e.preventDefault()
              setOverColumn(col.id)
            }}
            onDragLeave={() => setOverColumn((s) => (s === col.id ? null : s))}
            onDrop={(e) => {
              e.preventDefault()
              handleDrop(col)
            }}
            className={cn(
              'rounded-xl border-2 border-gray-300 bg-gray-100 p-1.5 min-h-[300px] flex flex-col gap-1.5 transition-colors',
              overColumn === col.id && 'border-indigo-500 bg-indigo-100',
            )}
          >
            <div
              className={cn(
                'flex items-center justify-between rounded-lg border px-3 py-2',
                col.accent ?? COLUMN_ACCENT[col.status],
              )}
            >
              <span className="text-sm font-bold truncate min-w-0 flex-1">{col.label}</span>
              <span className="text-xs font-bold bg-white/80 rounded-full px-2 py-0.5">
                {list.length}
              </span>
            </div>

            {list.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-6">{t('admin.kanban.empty')}</p>
            )}

            {(groupKey ? groupAdjacent(list, groupKey) : [{ key: null, items: list }]).map(
              (group, gi) =>
                group.key === null ? (
                  group.items.map((order) => (
                    <DraggableCard
                      key={order.id}
                      order={order}
                      dragId={dragId}
                      dropHint={dropHint}
                      setDragId={setDragId}
                      setOverColumn={setOverColumn}
                      setDropHint={setDropHint}
                      onDropCard={() => handleDrop(col)}
                    >
                      {renderCard(order)}
                    </DraggableCard>
                  ))
                ) : (
                  <div
                    key={`${group.key}-${gi}`}
                    className="rounded-xl border-2 border-gray-400 bg-gray-200/60 p-1.5 flex flex-col gap-2"
                  >
                    <div className="flex items-center gap-1.5 px-1 pt-0.5">
                      <span className="text-xs font-bold text-gray-800 truncate">{group.key}</span>
                      <span className="text-[10px] font-semibold text-gray-600 bg-white rounded-full px-1.5 py-0.5 flex-shrink-0">
                        {t('admin.kanban.order_count', { count: String(group.items.length) })}
                      </span>
                    </div>
                    {(subgroupKey
                      ? groupAdjacent(group.items, subgroupKey)
                      : [{ key: null, items: group.items }]
                    ).map((sub, si) =>
                      sub.key === null ? (
                        sub.items.map((order) => (
                          <DraggableCard
                            key={order.id}
                            order={order}
                            dragId={dragId}
                            dropHint={dropHint}
                            setDragId={setDragId}
                            setOverColumn={setOverColumn}
                            setDropHint={setDropHint}
                            onDropCard={() => handleDrop(col)}
                          >
                            {renderCard(order)}
                          </DraggableCard>
                        ))
                      ) : (
                        <div
                          key={`${sub.key}-${si}`}
                          className="rounded-lg border-2 border-dashed border-indigo-300 bg-white/70 p-1.5 flex flex-col gap-2"
                        >
                          <div className="flex items-center gap-1.5 px-1 pt-0.5 min-w-0">
                            <span className="text-xs font-bold text-indigo-800 truncate min-w-0">
                              👥 {sub.key}
                            </span>
                            <span className="text-[10px] font-semibold text-indigo-500 bg-white rounded-full px-1.5 py-0.5 flex-shrink-0">
                              {sub.items.length}×
                            </span>
                          </div>
                          {sub.items.map((order) => (
                            <DraggableCard
                              key={order.id}
                              order={order}
                              dragId={dragId}
                              dropHint={dropHint}
                              setDragId={setDragId}
                              setOverColumn={setOverColumn}
                              setDropHint={setDropHint}
                              onDropCard={() => handleDrop(col)}
                            >
                              {renderCard(order)}
                            </DraggableCard>
                          ))}
                        </div>
                      ),
                    )}
                  </div>
                ),
            )}
          </div>
        )
      })}
    </div>
  )
}

function DraggableCard({
  order,
  dragId,
  dropHint,
  setDragId,
  setOverColumn,
  setDropHint,
  onDropCard,
  children,
}: {
  order: OrderDetailed
  dragId: string | null
  dropHint: string | null
  setDragId: (id: string | null) => void
  setOverColumn: (id: string | null) => void
  setDropHint: (id: string | null) => void
  onDropCard: () => void
  children: ReactNode
}) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        setDragId(order.id)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', order.id)
      }}
      onDragEnd={() => {
        setDragId(null)
        setOverColumn(null)
        setDropHint(null)
      }}
      onDragOver={(e) => {
        e.preventDefault()
        e.stopPropagation()
        if (dragId && dragId !== order.id) setDropHint(order.id)
      }}
      onDrop={(e) => {
        e.stopPropagation()
        setDropHint(order.id)
        onDropCard()
      }}
      className={cn(
        'transition-opacity',
        dragId === order.id && 'opacity-40',
        dropHint === order.id && dragId !== order.id && 'border-t-2 border-t-indigo-400',
      )}
    >
      {children}
    </div>
  )
}
